import React, { useState, useEffect } from 'react'
import { backendService } from '../../services/backendService'
import { logInfo, logWarning } from '../../utils/errorLogger'
import './ConnectionStatus.css'

export const ConnectionStatus: React.FC = () => {
  const [isConnected, setIsConnected] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showDetails, setShowDetails] = useState(false)

  useEffect(() => {
    // Subscribe to connection status changes
    const unsubscribe = backendService.addConnectionListener((connected, message) => {
      setIsConnected(connected)
      setErrorMessage(message || null)
      
      if (!connected) {
        logWarning(`Backend disconnected: ${message || 'Unknown reason'}`, 'ConnectionStatus')
      } else {
        logInfo('Backend connection restored', 'ConnectionStatus')
      }
    })

    // Initial status check
    setIsConnected(backendService.getConnectionStatus())

    return unsubscribe
  }, [])

  // Don't show anything if connected
  if (isConnected) {
    return null
  }

  return (
    <div className="connection-status disconnected">
      <div
        className="status-indicator"
        onClick={() => setShowDetails(!showDetails)}
        title="Backend connection status"
      >
        <span className="status-icon">🔴</span>
        <span className="status-text">Server Disconnected</span>
      </div>
      
      {showDetails && (
        <div className="status-details">
          <div className="status-popup">
            <h4>Backend Connection Lost</h4>
            <p className="error-message">
              {errorMessage || 'Unable to connect to the backend server'}
            </p>
            <div className="status-actions">
              <button 
                className="retry-btn"
                onClick={() => {
                  logInfo('Manual health check triggered', 'ConnectionStatus')
                  backendService.healthCheck().catch(() => {
                    // Error already logged in health check
                  })
                }}
              >
                Retry Connection
              </button>
              <button 
                className="close-btn"
                onClick={() => setShowDetails(false)}
              >
                Close
              </button>
            </div>
            <div className="status-info">
              <p>
                <strong>What this means:</strong>
              </p>
              <ul>
                <li>Library data may not be up to date</li>
                <li>New doctors/patients cannot be added</li>
                <li>STL processing is unavailable</li>
                <li>Changes will not be saved</li>
              </ul>
              <p>
                <strong>Troubleshooting:</strong>
              </p>
              <ul>
                <li>Check your internet connection</li>
                <li>Verify the backend server is running</li>
                <li>Contact support if the issue persists</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
